export const softwareData: Record<
  string,
  {
    name: string;
    bandwidth: number;
    techLevel: number;
    cost: number;
    effect: string;
    hasLevels?: boolean;
    maxLevel?: number;
    hasRange?: boolean;
    hasCrewReplacement?: boolean;
  }
> = {
  MANOEUVRE: {
    name: "Manoeuvre",
    bandwidth: 0,
    techLevel: 8,
    cost: 0,
    effect: "Allows basic control of ship.",
  },
  INTELLECT: {
    name: "Intellect",
    bandwidth: 0,
    techLevel: 11,
    cost: 0,
    effect: "Allows a ship to understand and obey verbal commands.",
  },
  JUMP_CONTROL: {
    name: "Jump Control",
    bandwidth: 5,
    techLevel: 9,
    cost: 0.1,
    effect:
      "Allows jumps of up to the specified number. Incorporates astrogation software and jump engine management.",
    hasLevels: true,
    maxLevel: 6,
  },
  EVADE: {
    name: "Evade",
    bandwidth: 10,
    techLevel: 9,
    cost: 1,
    effect:
      "The computer reacts automatically to incoming fire, applying a negative DM equal to the Evade program's score to all attacks.",
    hasLevels: true,
    maxLevel: 3,
  },
  FIRE_CONTROL: {
    name: "Fire Control",
    bandwidth: 5,
    techLevel: 9,
    cost: 2,
    effect:
      "Allows the computer to fire a number of turrets per round equal to the listed number.",
    hasLevels: true,
    maxLevel: 5,
  },
  AUTO_REPAIR: {
    name: "Auto-Repair",
    bandwidth: 10,
    techLevel: 10,
    cost: 5,
    effect:
      "Allows the computer to make a number of repair attempts per round equal to the listed number.",
    hasLevels: true,
    maxLevel: 2,
  },
  LIBRARY: {
    name: "Library",
    bandwidth: 0,
    techLevel: 8,
    cost: 0,
    effect: "Contains a wealth of data on numerous subjects.",
  },
  ADVANCED_FIRE_CONTROL: {
    name: "Advanced Fire Control",
    bandwidth: 15,
    techLevel: 10,
    cost: 12,
    effect:
      "All weapons mounted upon the ship gain a DM to their attack rolls equal to the Advanced Fire Control package's score.",
    hasLevels: true,
    maxLevel: 3,
  },
  ANTI_HIJACK: {
    name: "Anti-Hijack",
    bandwidth: 2,
    techLevel: 11,
    cost: 6,
    effect:
      "Any skill check made to gain unauthorised entry to the ship's computer or any restricted area suffers a negative DM equal to double the Anti-Hijack's software score.",
    hasLevels: true,
    maxLevel: 3,
  },
  BATTLE_NETWORK: {
    name: "Battle Network",
    bandwidth: 5,
    techLevel: 12,
    cost: 5,
    effect:
      "Enables a ship to hand off target sensor detection data to all desired ships within the specified range.",
    hasLevels: true,
    maxLevel: 2,
    hasRange: true,
  },
  BATTLE_SYSTEM: {
    name: "Battle System",
    bandwidth: 5,
    techLevel: 9,
    cost: 18,
    effect:
      "Any Traveller using Battle System gains a DM to Tactics (naval) checks equal to the Battle System package's score.",
    hasLevels: true,
    maxLevel: 3,
  },
  BROAD_SPECTRUM_EW: {
    name: "Broad Spectrum EW",
    bandwidth: 12,
    techLevel: 13,
    cost: 14,
    effect:
      "A single electronic warfare action is automatically performed against any and all enemy salvoes launched within Long range.",
  },
  CONSCIOUS_INTELLIGENCE: {
    name: "Conscious Intelligence",
    bandwidth: 40,
    techLevel: 16,
    cost: 25,
    effect:
      "A fully aware, completely sentient digital being, capable of thought, deduction, analysis and emotion.",
    hasLevels: true,
    maxLevel: 3,
  },
  ELECTRONIC_WARFARE: {
    name: "Electronic Warfare",
    bandwidth: 10,
    techLevel: 10,
    cost: 15,
    effect:
      "All electronic warfare actions performed from the ship gain a DM to their Electronics (sensors) checks equal to the Electronic Warfare package's score.",
    hasLevels: true,
    maxLevel: 3,
  },
  LAUNCH_SOLUTION: {
    name: "Launch Solution",
    bandwidth: 5,
    techLevel: 8,
    cost: 10,
    effect:
      "All missile and torpedo salvoes fired by the ship gain a DM to their attack rolls equal to the Launch Solution package's score.",
    hasLevels: true,
    maxLevel: 3,
  },
  POINT_DEFENCE: {
    name: "Point Defence",
    bandwidth: 12,
    techLevel: 9,
    cost: 8,
    effect:
      "A ship running the Point Defence package may use point defence batteries and the Point Defence (gunner) reaction to defend any ship within Close range.",
    hasLevels: true,
    maxLevel: 2,
    hasRange: true,
  },
  SCREEN_OPTIMISER: {
    name: "Screen Optimiser",
    bandwidth: 10,
    techLevel: 10,
    cost: 5,
    effect:
      "Automatically performs the Angle Screens (gunner) action with a total DM+0, against any attack and can use any number of screens simultaneously.",
  },
  VIRTUAL_CREW: {
    name: "Virtual Crew",
    bandwidth: 5,
    techLevel: 10,
    cost: 1,
    effect:
      "Can replace up to five pilots, gunners or sensor operators on board a ship.",
    hasLevels: true,
    maxLevel: 2,
    hasCrewReplacement: true,
  },
  VIRTUAL_GUNNER: {
    name: "Virtual Gunner",
    bandwidth: 5,
    techLevel: 9,
    cost: 1,
    effect:
      "Allows a ship's computer to replace living gunnery crew in an efficient manner.",
    hasLevels: true,
    maxLevel: 2,
    hasCrewReplacement: true,
  },
  INTERFACE: {
    name: "Interface",
    bandwidth: 0,
    techLevel: 7,
    cost: 0,
    effect: "Displays data.",
  },
  INTELLIGENT_INTERFACE: {
    name: "Intelligent Interface",
    bandwidth: 1,
    techLevel: 11,
    cost: 0.0001,
    effect:
      "Artificial intelligence allows voice control and displays data intelligently. Required for using Expert software.",
  },
  SECURITY: {
    name: "Security",
    bandwidth: 0,
    techLevel: 8,
    cost: 0,
    effect: "Security software packages defend against intrusion.",
    hasLevels: true,
    maxLevel: 3,
  },
  INTRUSION: {
    name: "Intrusion",
    bandwidth: 1,
    techLevel: 10,
    cost: 0.0001,
    effect:
      "Intrusion software packages aid hacking attempts, giving a bonus equal to their Bandwidth.",
    hasLevels: true,
    maxLevel: 4,
  },
  EXPERT: {
    name: "Expert",
    bandwidth: 1,
    techLevel: 11,
    cost: 0.0001,
    effect:
      "Expert software packages mimic skills. A Traveller using Expert may make a skill check as if they had the skill at the software's Bandwidth -1.",
    hasLevels: true,
    maxLevel: 3,
  },
  TRANSLATOR: {
    name: "Translator",
    bandwidth: 0,
    techLevel: 9,
    cost: 0.00005,
    effect:
      "Translators are specialised Expert packages that only have Language skills.",
    hasLevels: true,
    maxLevel: 1,
  },
  DATABASE: {
    name: "Database",
    bandwidth: 0,
    techLevel: 7,
    cost: 0.00001,
    effect:
      "A database is a large store of information on a topic that can be searched with an Electronics (computers) check or using an Agent.",
    hasLevels: true,
    maxLevel: 3,
  },
  AGENT: {
    name: "Agent",
    bandwidth: 0,
    techLevel: 11,
    cost: 0.0005,
    effect:
      "Agent packages have an Electronics (computers) skill equal to their Bandwidth and can carry out tasks assigned to them with a modicum of intelligence.",
    hasLevels: true,
    maxLevel: 3,
  },
};

// Traveller Ship Optional Systems (all categories)
export const OPTIONAL_SYSTEM_TYPES = [
  // Example entries (replace with full data as needed):
  // Structure
  {
    id: "armouredBulkhead",
    type: "structure",
    subtype: "armouredBulkhead",
    name: "Armoured Bulkhead",
    description:
      "Armoured bulkheads protect specific areas and systems, such as the jump drive or fuel tanks, making them much more resilient to damage. Consumes 10% of the tonnage of the protected item. Reduces critical hit severity by 1 (min 1) for protected space.",
    cost: 0, // Calculated as MCr0.2 per ton protected
    tonnage: 0, // Calculated as 10% of protected system
    power: 0,
    techLevel: 12,
    properties: {
      costPerTon: 200000, // Cr0.2M per ton
      tonnageMultiplier: 0.1, // 10% of protected system
      protection: true,
    },
  },
  {
    id: "adjustableHull12",
    type: "structure",
    subtype: "adjustableHull",
    name: "Adjustable Hull (TL 12)",
    description:
      "Hull sections can reconfigure to mimic other ships of the same tonnage and configuration. Reconfiguration takes one round, during which the ship cannot fire, dock, launch, or use sensors. All weapons have pop-up mountings at no extra cost.",
    cost: 0, // Calculated as +10% base hull cost
    tonnage: 0, // Calculated as 5% of ship tonnage
    power: 0,
    techLevel: 12,
    properties: {
      tonnageMultiplier: 0.05, // 5% of ship tonnage
      costMultiplier: 0.1, // +10% base hull cost
      reconfigurationTime: 1, // 1 round
      popUpMounts: true,
    },
  },
  {
    id: "adjustableHull15",
    type: "structure",
    subtype: "adjustableHull",
    name: "Adjustable Hull (TL 15)",
    description:
      "Hull sections can reconfigure to mimic other ships of the same tonnage and configuration. Reconfiguration takes one round, during which the ship cannot fire, dock, launch, or use sensors. All weapons have pop-up mountings at no extra cost.",
    cost: 0, // Calculated as +100% base hull cost
    tonnage: 0, // Calculated as 1% of ship tonnage
    power: 0,
    techLevel: 15,
    properties: {
      tonnageMultiplier: 0.01, // 1% of ship tonnage
      costMultiplier: 1.0, // +100% base hull cost
      reconfigurationTime: 1, // 1 round
      popUpMounts: true,
    },
  },
  {
    id: "pressureHull",
    type: "structure",
    subtype: "pressureHull",
    name: "Pressure Hull",
    description:
      "Designed for extreme pressure environments. Allows safe operation in gas giant deeps and underwater up to 50 km. Provides intrinsic Armour +4. Consumes 25% of vessel's tonnage and costs 10x normal hull cost.",
    cost: 0, // Calculated as 10x base hull cost
    tonnage: 0, // Calculated as 25% of ship tonnage
    power: 0,
    techLevel: 12,
    properties: {
      tonnageMultiplier: 0.25, // 25% of ship tonnage
      costMultiplier: 10.0, // 10x base hull cost
      pressureResistance: true,
      armourBonus: 4,
    },
  },
  // Modular Hull
  {
    id: "modularBay",
    type: "modularHull",
    subtype: "modularSpace",
    name: "Modular Bay",
    description:
      "A modular bay that can be reconfigured for different purposes.",
    cost: 500000,
    tonnage: 20,
    power: 2,
    techLevel: 12,
    properties: {
      percentageOfShip: 20,
      isMainHull: false,
      moduleCost: 100000,
      moduleOptions: [
        "cargoBay",
        "passengerBay",
        "hangarBay",
        "workshop",
        "medicalBay",
      ],
    },
  },
  // Power
  {
    id: "auxiliaryPowerUnit",
    type: "power",
    subtype: "auxiliary",
    name: "Auxiliary Power Unit",
    description: "Backup power source for emergencies.",
    cost: 200000,
    tonnage: 1,
    power: 1,
    techLevel: 12,
    properties: {},
  },
  {
    id: "solarPanelsBasic",
    type: "power",
    subtype: "solarPanels",
    name: "Solar Panels (Basic)",
    description:
      "Extendible solar panels providing backup power. Each unit is 1 ton, max as desired. Only provide power when deployed. Cannot accelerate while deployed. Not for streamlined hulls. Output and cost depend on TL.",
    cost: 100000, // MCr0.1 per unit (TL6)
    tonnage: 1, // 1 ton per unit
    power: 0, // Calculated per unit
    techLevel: 6,
    properties: {
      costPerUnit: {
        6: 100000, // MCr0.1
        8: 200000, // MCr0.2
        10: 300000, // MCr0.3
        12: 400000, // MCr0.4
      },
      powerPerUnit: {
        6: 0.25,
        8: 0.5,
        10: 1,
        12: 2,
      },
      allowedHullTypes: [
        "standard",
        "sphere",
        "closeStructure",
        "dispersedStructure",
      ],
      disallowedHullTypes: ["streamlined", "planetoid", "bufferedPlanetoid"],
      deploymentTime: 1, // 1 turn
      detectionDM: 2, // DM+2 when deployed
      backupOnly: true,
    },
  },
  {
    id: "solarPanelsImproved",
    type: "power",
    subtype: "solarPanels",
    name: "Solar Panels (Improved)",
    description:
      "Extendible solar panels providing backup power. Each unit is 1 ton, max as desired. Only provide power when deployed. Cannot accelerate while deployed. Not for streamlined hulls. Output and cost depend on TL.",
    cost: 200000, // MCr0.2 per unit (TL8)
    tonnage: 1,
    power: 0,
    techLevel: 8,
    properties: {
      costPerUnit: {
        8: 200000,
        10: 300000,
        12: 400000,
      },
      powerPerUnit: {
        8: 0.5,
        10: 1,
        12: 2,
      },
      allowedHullTypes: [
        "standard",
        "sphere",
        "closeStructure",
        "dispersedStructure",
      ],
      disallowedHullTypes: ["streamlined", "planetoid", "bufferedPlanetoid"],
      deploymentTime: 1,
      detectionDM: 2,
      backupOnly: true,
    },
  },
  {
    id: "solarPanelsEnhanced",
    type: "power",
    subtype: "solarPanels",
    name: "Solar Panels (Enhanced)",
    description:
      "Extendible solar panels providing backup power. Each unit is 1 ton, max as desired. Only provide power when deployed. Cannot accelerate while deployed. Not for streamlined hulls. Output and cost depend on TL.",
    cost: 300000, // MCr0.3 per unit (TL10)
    tonnage: 1,
    power: 0,
    techLevel: 10,
    properties: {
      costPerUnit: {
        10: 300000,
        12: 400000,
      },
      powerPerUnit: {
        10: 1,
        12: 2,
      },
      allowedHullTypes: [
        "standard",
        "sphere",
        "closeStructure",
        "dispersedStructure",
      ],
      disallowedHullTypes: ["streamlined", "planetoid", "bufferedPlanetoid"],
      deploymentTime: 1,
      detectionDM: 2,
      backupOnly: true,
    },
  },
  {
    id: "solarPanelsAdvanced",
    type: "power",
    subtype: "solarPanels",
    name: "Solar Panels (Advanced)",
    description:
      "Extendible solar panels providing backup power. Each unit is 1 ton, max as desired. Only provide power when deployed. Cannot accelerate while deployed. Not for streamlined hulls. Output and cost depend on TL.",
    cost: 400000, // MCr0.4 per unit (TL12)
    tonnage: 1,
    power: 0,
    techLevel: 12,
    properties: {
      costPerUnit: {
        12: 400000,
      },
      powerPerUnit: {
        12: 2,
      },
      allowedHullTypes: [
        "standard",
        "sphere",
        "closeStructure",
        "dispersedStructure",
      ],
      disallowedHullTypes: ["streamlined", "planetoid", "bufferedPlanetoid"],
      deploymentTime: 1,
      detectionDM: 2,
      backupOnly: true,
    },
  },
  {
    id: "emergencyPowerSystem",
    type: "power",
    subtype: "emergency",
    name: "Emergency Power System",
    description:
      "Allows a ship to keep functioning for 5 rounds at 90% power if the main power plant is taken offline by a critical hit (Severity 3+). Consumes 10% of power plant tons and costs 10% of power plant cost. If the power plant sustains a Severity 6 critical hit, emergency power is lost as well.",
    cost: 0, // Calculated as 10% of power plant cost
    tonnage: 0, // Calculated as 10% of power plant tons
    power: 0, // Not a power source, just a backup
    techLevel: 8,
    properties: {
      costMultiplier: 0.1, // 10% of power plant cost
      tonnageMultiplier: 0.1, // 10% of power plant tons
      emergency: true,
      duration: 5, // rounds
      powerPercent: 0.9, // 90% of normal power
      activation: "auto on critical hit (Severity 3+)",
    },
  },
  {
    id: "highEfficiencyBatteries",
    type: "power",
    subtype: "battery",
    name: "High-Efficiency Batteries",
    description:
      "Ship-board batteries store power for later use. Can be recharged in any round with excess Power, and discharged in subsequent rounds as if produced by the power plant. Can be combined with secondary power systems (e.g., solar) to keep charged. User selects number of tons.",
    cost: 100000, // TL 10 default, per ton
    tonnage: 1, // 1 ton per unit
    power: 40, // TL 10 default, per ton
    techLevel: 10,
    properties: {
      costPerUnit: {
        10: 100000, // MCr0.1 per ton
        12: 200000, // MCr0.2 per ton
      },
      powerPerUnit: {
        10: 40, // 40 Power per ton
        12: 60, // 60 Power per ton
      },
      userSelectableTonnage: true,
      userSelectableTL: true,
      rechargeable: true,
      storage: true,
      minTL: 10,
      maxTL: 12,
    },
  },
  // Drive - Canonical Traveller Options
  {
    id: "highBurnThruster",
    type: "drive",
    subtype: "highBurn",
    name: "High-Burn Thruster",
    description:
      "Auxiliary chemical rocket for temporary speed boost. Adds cumulative thrust with regular drive system. Requires far less fuel than main reaction drives. Note: No inertial compensation - crew subject to G-force effects.",
    cost: 0, // Calculated based on thrust rating
    tonnage: 0, // Calculated based on thrust rating
    power: 0, // No power requirement
    techLevel: 8,
    properties: {
      userSelectableThrust: true,
      minThrust: 1,
      maxThrust: 6,
      costPerThrust: 500000, // MCr0.5 per thrust rating
      tonnagePerThrust: 0.5, // 0.5 tons per thrust rating
      fuelEfficiency: 0.1, // 10% of normal reaction drive fuel consumption
      temporaryUse: true,
      noInertialCompensation: true,
      cumulativeThrust: true,
    },
  },
  {
    id: "concealedManoeuvreDrive",
    type: "drive",
    subtype: "concealed",
    name: "Concealed Manoeuvre Drive",
    description:
      "Manoeuvre drive thruster plates concealed behind bulkheads for stealth. Must be within 3m of accelerating surface. Adds 25% to drive tons/cost, halves thrust performance (round down).",
    cost: 0, // Calculated as +25% of M-drive cost
    tonnage: 0, // Calculated as +25% of M-drive tons
    power: 0, // No additional power requirement
    techLevel: 8,
    properties: {
      costMultiplier: 0.25, // +25% of M-drive cost
      tonnageMultiplier: 0.25, // +25% of M-drive tons
      thrustMultiplier: 0.5, // Halves thrust (round down)
      stealth: true,
      maxDistanceFromHull: 3, // meters
      requiresManeuverDrive: true,
    },
  },
  {
    id: "solarSail",
    type: "drive",
    subtype: "solarSail",
    name: "Solar Sail",
    description:
      "Flexible synthetic fabric sail using solar wind for propulsion. Requires no power or reaction mass. Effective Thrust 0, several days to change course/speed. Cannot jump while deployed. Covers dozens of kilometers when deployed.",
    cost: 0, // Calculated as MCr0.2 per ton
    tonnage: 0, // Calculated as 5% of hull tonnage
    power: 0, // No power requirement
    techLevel: 6,
    properties: {
      costPerTon: 200000, // MCr0.2 per ton
      tonnagePercent: 0.05, // 5% of hull tonnage
      effectiveThrust: 0,
      noPowerRequired: true,
      noReactionMass: true,
      deploymentTime: "several days",
      jumpRestriction: true,
      deploymentSize: "dozens of kilometers",
      selfRepair: true,
    },
  },
  // Fuel
  {
    id: "collapsibleTank",
    type: "fuel",
    subtype: "collapsibleTank",
    name: "Collapsible Fuel Tank",
    description: "Flexible bladders for extra fuel, use cargo space.",
    cost: 500000, // Cr500 per ton (Rules Reference: Cr500 per ton)
    tonnage: 5,
    power: 0,
    techLevel: 12,
    properties: { fullTonnage: 5, emptyTonnage: 0.05 },
  },
  // Accommodation
  {
    id: "accelerationBench",
    type: "accommodation",
    subtype: "accelerationBench",
    name: "Acceleration Bench",
    description:
      "Basic padded bench seating for 4 passengers, folds away when not in use.",
    cost: 10000,
    tonnage: 1,
    power: 0,
    techLevel: 8,
    properties: { seats: 4 },
  },
  {
    id: "accelerationSeat",
    type: "accommodation",
    subtype: "accelerationSeat",
    name: "Acceleration Seat",
    description:
      "Comfier padded seat for 1 passenger, less space efficient than bench.",
    cost: 30000,
    tonnage: 0.5,
    power: 0,
    techLevel: 8,
    properties: { seats: 1 },
  },
  {
    id: "barracks",
    type: "accommodation",
    subtype: "barracks",
    name: "Barracks",
    description:
      "Cramped bunks for military or basic passengers. 1 passenger per ton.",
    cost: 50000, // per ton
    tonnage: 1, // per passenger
    power: 0,
    techLevel: 8,
    properties: {
      userSelectableTonnage: true,
      costPerTonnage: 50000,
      lifeSupportCostPerTon: 500,
    },
  },
  {
    id: "brig",
    type: "accommodation",
    subtype: "brig",
    name: "Brig",
    description:
      "Secure chamber with reinforced walls and manually locked door. Holds up to 6 prisoners comfortably, or 12 uncomfortably. No internal facilities except curtained fresher and 6 pull-down bed slabs. Can double as temporary office or storage area.",
    cost: 250000, // MCr0.25
    tonnage: 4,
    power: 0,
    techLevel: 8,
    properties: { prisoners: 6, maxPrisoners: 12, lifeSupportCost: 1000 },
  },
  {
    id: "cabinSpace",
    type: "accommodation",
    subtype: "cabinSpace",
    name: "Cabin Space",
    description:
      "Basic cabin space for moderate comfort. 1 passenger per 1.5 tons.",
    cost: 50000, // per ton
    tonnage: 1.5, // per passenger
    power: 0,
    techLevel: 8,
    properties: {
      userSelectableTonnage: true,
      costPerTonnage: 50000,
      lifeSupportCostPerTon: 250,
    },
  },
  {
    id: "multiEnvironmentSpace",
    type: "accommodation",
    subtype: "multiEnvironmentSpace",
    name: "Multi-Environment Space",
    description:
      "Space with environmental controls for exotic life. +5% tonnage for equipment.",
    cost: 500000, // per ton (MCr0.5)
    tonnage: 1, // user input
    power: 1, // per ton
    techLevel: 12,
    properties: {
      userSelectableTonnage: true,
      costPerTonnage: 500000,
      powerPerTon: 1,
      tonnageMultiplier: 1.05,
    },
  },
  {
    id: "highStateroom",
    type: "accommodation",
    subtype: "highStateroom",
    name: "High Stateroom",
    description:
      "Spacious, finely appointed stateroom for high passengers. 6 tons.",
    cost: 800000, // MCr0.8
    tonnage: 6,
    power: 0,
    techLevel: 12,
    properties: { lifeSupportCost: 3000, highPassengerDM: 1 },
  },
  {
    id: "luxuryStateroom",
    type: "accommodation",
    subtype: "luxuryStateroom",
    name: "Luxury Stateroom",
    description:
      "State-of-the-art luxury accommodation for the most discerning passenger. 10 tons.",
    cost: 1500000, // MCr1.5
    tonnage: 10,
    power: 0,
    techLevel: 12,
    properties: { lifeSupportCost: 5000, highPassengerDM: 2 },
  },
  // Bridge
  {
    id: "detachableBridge",
    type: "bridge",
    subtype: "detachable",
    name: "Detachable Bridge",
    description:
      "This bridge design can be ejected from the ship in an emergency to become a lifeboat for the command crew. The bridge has two weeks of life support and battery power, while emergency thrusters give it basic manoeuvring capabilities, equivalent to Thrust 0. A detachable bridge is even capable of soft-landing on a planetary surface.",
    cost: 0, // Calculated as +50% of base bridge cost
    tonnage: 0, // Calculated as +20% of base bridge tonnage
    power: 0,
    techLevel: 8,
    properties: {
      costMultiplier: 0.5, // +50% to base bridge cost
      tonnageMultiplier: 0.2, // +20% to base bridge tonnage
      lifeSupportWeeks: 2,
      emergencyThrust: 0,
      softLandingCapable: true,
      minimumSizes: {
        200: 15, // 200 tons or less: 15 tons minimum
        1000: 30, // 201-1,000 tons: 30 tons minimum
        2000: 50, // 1,001-2,000 tons: 50 tons minimum
        Infinity: 80, // 2,001 tons or more: 80 tons minimum
      },
    },
  },
  {
    id: "holographicControls",
    type: "bridge",
    subtype: "holographic",
    name: "Holographic Controls",
    description:
      "This bridge design incorporates advanced interactive holographic displays and can be configured to automatically adapt to changing situations or personnel. A bridge with holographic controls is always optimised for the task at hand and grants DM+2 when rolling for initiative.",
    cost: 0, // Calculated as +25% of base bridge cost
    tonnage: 0, // No additional tonnage
    power: 1, // Additional power for holographic systems
    techLevel: 9,
    properties: {
      costMultiplier: 0.25, // +25% to base bridge cost
      initiativeDM: 2,
      adaptiveControls: true,
      interactiveDisplays: true,
    },
  },
  {
    id: "sensorStation",
    type: "bridge",
    subtype: "sensorStation",
    name: "Sensor Station",
    description:
      "Additional station to handle an increased number of sensor operators. During space combat, sensor operators can perform different tasks including detecting enemy ships, obtaining sensor locks and performing electronic warfare. Each additional sensor station can perform a different task in the same combat round.",
    cost: 500000, // MCr0.5
    tonnage: 1,
    power: 0.5,
    techLevel: 8,
    properties: {
      maxShipTonnage: 7500, // Only available on ships of 7,500 tons or less
      sensorOperatorCapacity: 1,
      combatTasks: ["detectEnemy", "sensorLock", "electronicWarfare"],
      description:
        "Note that sensor stations are options only on ships of 7,500 tons or less. Capital ships are assumed to have multiple sensor stations in their much larger bridges.",
    },
  },
  // Cargo
  {
    id: "cargoCrane",
    type: "cargo",
    subtype: "cargoCrane",
    name: "Cargo Crane",
    description:
      "Built into the ceiling of a cargo hold, this overhead gantry crane is designed to shift cargo containers in and out of the ship. The crane's mechanism moves about the bay on a sliding jig and can extend beyond the cargo door on a gibbet to deposit freight directly onto a dockside or vehicle. The crane is strong enough to lift fully loaded containers of up to 65 tons and can couple with most pallets and crates.",
    cost: 0, // Calculated as MCr1 per ton
    tonnage: 0, // Calculated as 2.5 + 0.5 per 150 tons (or part thereof) of cargo space
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 1000000, // MCr1 per ton
      baseTonnage: 2.5,
      tonnagePer150Tons: 0.5,
      maxLiftCapacity: 65, // tons
    },
  },
  {
    id: "cargoNet",
    type: "cargo",
    subtype: "cargoNet",
    name: "Cargo Net",
    description:
      "A slower but safer method than a scoop for retrieving cargo from space, the net consists of a mesh of tough plastic and several remotely controlled tow drones. The drones are launched from the ship, dragging the net out behind them. The net is drawn around a volume of space and then retracted. The plastic is extremely elastic, allowing the volume of the net to be potentially increased to a dozen cubic kilometres. Note that a starship cannot jump with its cargo net extended.",
    cost: 1000000, // MCr1
    tonnage: 5,
    power: 0,
    techLevel: 8,
    properties: {
      jumpRestriction: true,
      volumeIncrease: "dozen cubic kilometres",
      retrievalMethod: "tow drones",
    },
  },
  {
    id: "cargoScoop",
    type: "cargo",
    subtype: "cargoScoop",
    name: "Cargo Scoop",
    description:
      "A cargo scoop allows a ship to pick up cargo or other objects floating in space. The scoop includes antigravity baffles to minimise the impact but using a scoop with a high relative velocity is not recommended. A Pilot check is required to scoop up an object; if failed, the ship collides with the object, taking an amount of damage equal to the negative Effect. Armour negates this damage, so most accidents are embarrassing rather than catastrophic and merely require a little repainting. A scoop can sweep up one ton of material per round.",
    cost: 500000, // MCr0.5
    tonnage: 2,
    power: 0,
    techLevel: 8,
    properties: {
      pilotCheckRequired: true,
      collectionRate: 1, // tons per round
      antigravityBaffles: true,
      damageNegatedByArmour: true,
    },
  },
  {
    id: "externalCargoMount",
    type: "cargo",
    subtype: "externalCargoMount",
    name: "External Cargo Mount",
    description:
      "Instead of carrying cargo inside the hull, a ship can be designed to mount cargo in an external rack or framework. This allows a smaller hull to be used, making the cost of transportation considerably cheaper. Ships with streamlined or dispersed structure hull configurations cannot use external cargo mounts. A ship's Thrust and jump capability must be recalculated when using external cargo mounts, using the combined tonnage of the ship and the external cargo it carries. Cargo carried externally can only be accessed outside the ship by vacc suit or vehicle. Landing a ship with external cargo mounts is a dangerous procedure, requiring a Difficult (10+) Pilot check. Failure results in a Critical Hit to the cargo. External cargo can be jettisoned remotely. The ship becomes unstreamlined regardless of its default configuration.",
    cost: 0, // Calculated as Cr1000 for every ton of cargo
    tonnage: 0, // No additional tonnage, uses external space
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 1000, // Cr1000 per ton
      requiresStreamlined: false,
      requiresDispersedStructure: false,
      makesUnstreamlined: true,
      pilotCheckRequired: true,
      pilotCheckDifficulty: 10,
      externalAccessOnly: true,
      jettisonable: true,
      affectsThrustAndJump: true,
    },
  },
  {
    id: "jumpNetInterplanetary",
    type: "cargo",
    subtype: "jumpNetInterplanetary",
    name: "Jump Net",
    description:
      "A jump net consists of special field cables attached to the rear of a ship that extend the vessel's jump field to include additional cargo contained within the net. While this can look exceedingly crude and makes the ship unstreamlined while in use, a great deal of engineering goes into making it work and it remains both cheap and effective. A cheaper version designed purely for interplanetary use is available at TL8 but the ship cannot perform a jump while this net is deployed. A ship using a jump net must have its drive potentials adjusted by adding the cargo carried to the ship's total hull mass.",
    cost: 0, // Calculated as MCr0.1 per ton
    tonnage: 0, // Calculated as 1 per 100 tons of cargo
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 100000, // MCr0.1 per ton
      tonnagePer100Tons: 1,
      jumpRestriction: true,
      makesUnstreamlined: true,
      affectsThrustAndJump: true,
      interplanetaryOnly: true,
    },
  },
  {
    id: "jumpNetInterstellar",
    type: "cargo",
    subtype: "jumpNetInterstellar",
    name: "Jump Net",
    description:
      "A jump net consists of special field cables attached to the rear of a ship that extend the vessel's jump field to include additional cargo contained within the net. While this can look exceedingly crude and makes the ship unstreamlined while in use, a great deal of engineering goes into making it work and it remains both cheap and effective. A ship using a jump net must have its drive potentials adjusted by adding the cargo carried to the ship's total hull mass.",
    cost: 0, // Calculated as MCr0.3 per ton
    tonnage: 0, // Calculated as 1 per 100 tons of cargo
    power: 0,
    techLevel: 10,
    properties: {
      costPerTon: 300000, // MCr0.3 per ton
      tonnagePer100Tons: 1,
      makesUnstreamlined: true,
      affectsThrustAndJump: true,
      interstellarCapable: true,
    },
  },
  {
    id: "loadingBelt7",
    type: "cargo",
    subtype: "loadingBelt7",
    name: "Loading Belt",
    description:
      "Used to offload cargo from a hold, the loading belt does the work of 10 crewmen when used properly.",
    cost: 3000, // Cr3000
    tonnage: 1,
    power: 1,
    techLevel: 7,
    properties: {
      crewReplacement: 10,
    },
  },
  {
    id: "loadingBelt12",
    type: "cargo",
    subtype: "loadingBelt12",
    name: "Loading Belt",
    description:
      "Used to offload cargo from a hold, the loading belt does the work of 25 crewmen when used properly. From TL12, high-powered magnets are used to propel cargo containers, increasing the work output.",
    cost: 10000, // Cr10000
    tonnage: 1,
    power: 1,
    techLevel: 12,
    properties: {
      crewReplacement: 25,
      highPoweredMagnets: true,
    },
  },
  // Drone
  {
    id: "repairDrone",
    type: "drone",
    subtype: "repair",
    name: "Repair Drone",
    description:
      "Repair drones (TL10+) allow remote repairs during combat. 1% of ship tonnage (min 1 ton), MCr0.2/ton.",
    cost: 0, // Calculated: MCr0.2 per ton, 1% of ship tonnage (min 1 ton)
    tonnage: 0, // Calculated: 1% of ship tonnage (min 1 ton)
    power: 0,
    techLevel: 10,
    properties: {
      coveragePercent: 1,
      minTonnage: 1,
      costPerTon: 200000,
      remoteRepair: true,
      combatRepairs: true,
    },
  },
  // Mining Drone
  {
    id: "miningDrone",
    type: "drone",
    subtype: "mining",
    name: "Mining Drone",
    description:
      "Set of 5 mining drones (TL12) for asteroid mining. Each set processes 5D tons of asteroid per day. Includes ore handling machinery.",
    cost: 1000000, // MCr1 per 10 tons
    tonnage: 10, // 5 drones per 10 tons
    power: 0, // Not specified in rules
    techLevel: 12,
    properties: {
      quantity: 5,
      processingRate: "5D tons/day",
      oreHandling: true,
      robotHits: 50,
      locomotion: "Thruster (0.1G)",
      attacks: "None",
      armour: "+10",
    },
  },
  // Probe Drone
  {
    id: "probeDrone",
    type: "drone",
    subtype: "probe",
    name: "Probe Drone",
    description:
      "Set of 5 probe drones (TL9) for planetary and orbital survey. Disposable entry shells, must be recovered manually. 10-year solar panels, 48h battery.",
    cost: 500000, // MCr0.5 per 5 drones (1 ton)
    tonnage: 1,
    power: 0,
    techLevel: 9,
    properties: {
      quantity: 5,
      operation: "planetary/orbital survey",
      solarPanel: "10-year half-life",
      battery: "48h backup",
      entryShell: true,
    },
  },
  // Advanced Probe Drone
  {
    id: "advancedProbeDrone",
    type: "drone",
    subtype: "advancedProbe",
    name: "Advanced Probe Drone",
    description:
      "Set of 5 advanced probe drones (TL12) with enhanced sensors. 25-year solar panels, 72h battery.",
    cost: 800000, // MCr0.8 per 5 drones (1 ton)
    tonnage: 1,
    power: 0,
    techLevel: 12,
    properties: {
      quantity: 5,
      operation: "advanced survey",
      solarPanel: "25-year half-life",
      battery: "72h backup",
      enhancedSensors: true,
    },
  },
  // External Systems
  // Aerofins
  {
    id: "aerofins",
    type: "external",
    subtype: "aerofins",
    name: "Aerofins",
    description:
      "Extendible aerofins improve a spacecraft's manoeuvrability in atmosphere, giving a far greater degree of control over its movements. A ship with aerofins deployed gains DM+2 to all Pilot checks when within an atmosphere.",
    cost: 0, // Calculated as MCr0.1 per ton
    tonnage: 0, // Calculated as 5% of ship tonnage
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 100000, // MCr0.1 per ton
      tonnagePercent: 5, // 5% of ship tonnage
      pilotDM: 2,
      atmosphericOnly: true,
    },
  },
  // Breaching Tube
  {
    id: "breachingTube",
    type: "external",
    subtype: "breachingTube",
    name: "Breaching Tube",
    description:
      "A military version of the airlock that may be forcibly applied to the hull of another ship. Instead of a thin myomer, the breaching tube is made of a combination of ballistic cloth and reflec. The breaching tube does not end in a docking collar but in a magnetic clamp with a ring of plasma torches that burn through the hull of an enemy vessel when attached. Breaching tubes can only be attached to disabled or otherwise inert ships. If either ship moves after a breaching tube has been attached, the breaching tube is destroyed and the ship to which it is attached receives 2D damage. An attacking ship fitted with a breaching tube gains DM+1 to rolls made on the Boarding Actions table.",
    cost: 3000000, // MCr3
    tonnage: 3,
    power: 0,
    techLevel: 8,
    properties: {
      boardingDM: 1,
      requiresDisabledTarget: true,
      damageOnMovement: "2D",
      plasmaTorches: true,
      magneticClamp: true,
    },
  },
  // Docking Clamp
  {
    id: "dockingClamp",
    type: "external",
    subtype: "dockingClamp",
    name: "Docking Clamp",
    description:
      "A docking clamp allows a spacecraft to carry another vessel on the exterior of its hull, typically a small craft such as a ship's boat or fighter. This permits a ship to carry auxiliary craft without consuming valuable interior space. A ship's Thrust and jump capability must be recalculated when another ship occupies its docking clamp, using the combined tonnage of both ships. Docking clamps can be added to streamlined ships but the ship becomes unstreamlined when another ship is docked to it. It takes three full rounds to release or clamp a ship of less than 2,000 tons, during which time neither ship can expand any Thrust or make any attack rolls. Larger ships take D3+3 rounds.",
    cost: 0, // Calculated based on type
    tonnage: 0, // Calculated based on type
    power: 0,
    techLevel: 8,
    properties: {
      clampType: "typeI", // Default, will be set by user
      maxAttachedTonnage: 30, // Default for Type I
      makesUnstreamlinedWhenDocked: true,
      releaseTime: "3 rounds", // for ships < 2000 tons
      releaseTimeLarge: "D3+3 rounds", // for ships >= 2000 tons
      affectsThrustAndJump: true,
    },
  },
  // Forced Linkage Apparatus
  {
    id: "forcedLinkage",
    type: "external",
    subtype: "forcedLinkage",
    name: "Forced Linkage Apparatus",
    description:
      "Used by naval patrol ships, scavengers and pirates to board spacecraft, forced linkage apparatuses are close-range grappling devices that allow attempts at forced docking. After a successful grapple, the ship can be boarded or towed into port at leisure. The ship attempting forced linkage must have a Thrust advantage of at least one over the opposing ship and may not be less than half the tonnage of the target ship. The use of Forced Linkage requires a task chain. First the attacking ship's pilot must position their ship for the linkage attempt. If the target ship is crewed and powered, make an opposed Pilot check (DEX or INT) against the pilot of the target ship. Each unused point of Thrust may be applied for DM+1. After the pilot of the attacking ship has won an opposed check, a gunner fires the grappling device: Average (8+) Gunner (turret) check (DEX), adding the Task Chain DM.",
    cost: 0, // Calculated based on type
    tonnage: 2,
    power: 0,
    techLevel: 7,
    properties: {
      linkageType: "basic", // Default, will be set by user
      pilotDM: -2, // Default for Basic
      maxTargetTonnage: 5000, // Can only be used on ships of 5,000 tons or less
      requiresThrustAdvantage: 1,
      requiresHalfTonnage: true,
      taskChainRequired: true,
      gunnerCheck: "Average (8+)",
      canCombineWithBreachingTube: true,
    },
  },
  // Grappling Arm
  {
    id: "grapplingArm",
    type: "external",
    subtype: "grapplingArm",
    name: "Grappling Arm",
    description:
      "A remotely controlled device used to pick up or manipulate objects in space. The arm is a flexible tentacle of thousands of telescoping segments, capable of reaching out to 250 metres. The arm ends in a set of cameras and grippers of varying sizes, from large claws to tiny micro-manipulators. It also carries a toolkit that can be customised for a particular task. A grappling arm can manipulate objects of up to two tons. A heavy grappling arm can manipulate objects of up to 10 tons. Multiple grappling arms of either type can be used to move heavier objects.",
    cost: 0, // Calculated based on type
    tonnage: 0, // Calculated based on type
    power: 0,
    techLevel: 8,
    properties: {
      armType: "standard", // Default, will be set by user
      maxWeight: 2, // Default for standard
      reach: 250, // metres
      telescopingSegments: true,
      cameras: true,
      grippers: true,
      toolkit: true,
      customizable: true,
    },
  },
  // Holographic Hull
  {
    id: "holographicHull",
    type: "external",
    subtype: "holographicHull",
    name: "Holographic Hull",
    description:
      "Multiple holographic projectors are embedded within the hull allowing the ship to change hull colours, add graphics and adopt a different appearance (although its shape remains the same). Changing the hull colour or adding a stored graphic can be done by anyone with access to the ship's computer. Creating a complex colour scheme from scratch requires the talents of a skilled artist. Some young nobles try to outdo one another by changing the schemes daily or hiring artists to create the most complex scenes imaginable. Some pirates have made use of this system to simulate visual hull damage and lure other ships in with a Signal GK distress call. However, most ship owners use it for advertising. The projectors are small enough that they do not consume tonnage.",
    cost: 0, // Calculated as Cr100,000 per ton of hull
    tonnage: 0, // No tonnage consumed
    power: 0, // Calculated as 1 per 2 tons of hull
    techLevel: 10,
    properties: {
      costPerHullTon: 100000, // Cr100,000 per ton of hull
      powerPerTwoHullTons: 1, // 1 power per 2 tons of hull
      noTonnageConsumed: true,
      computerControlled: true,
      artistRequired: true,
      advertisingUse: true,
      distressCallSimulation: true,
    },
  },
  // Tow Cable
  {
    id: "towCable",
    type: "external",
    subtype: "towCable",
    name: "Tow Cable",
    description:
      "A simple device used to haul an attached derelict or unpowered ship behind the modified vessel. The ship can potentially tow any size vessel provided it has the Thrust to do so. A ship's Thrust must be recalculated when it tows another ship or object, using the combined tonnage of both ships; therefore, the manoeuvre drive will be operating at a lower Thrust. A ship towing an object in this fashion cannot jump.",
    cost: 0, // Calculated as Cr5,000 per ton
    tonnage: 0, // Calculated as 1% of ship tonnage
    power: 0,
    techLevel: 7,
    properties: {
      costPerTon: 5000, // Cr5,000 per ton
      tonnagePercent: 1, // 1% of ship tonnage
      affectsThrust: true,
      preventsJump: true,
      canTowAnySize: true,
      requiresThrust: true,
    },
  },
  // Internal
  {
    id: "medicalBay",
    type: "internal",
    subtype: "internalMedicalBay",
    name: "Medical Bay",
    description: "Onboard medical treatment facility.",
    cost: 80000,
    tonnage: 3,
    power: 1,
    techLevel: 12,
    properties: {},
  },
  // Software
  {
    id: "evadeSoftware",
    type: "software",
    subtype: "evade",
    name: "Evade Software",
    description: "Automated evasion routines for ship defense.",
    cost: 10000,
    tonnage: 0,
    power: 0,
    techLevel: 12,
    properties: {},
  },
  // Countermeasures Suite
  {
    id: "countermeasuresSuite",
    type: "sensor",
    subtype: "countermeasuresSuite",
    name: "Countermeasures Suite",
    description:
      "Jams enemy transmissions (except meson). DM+4 to all jamming/electronic warfare. TL13.",
    cost: 4000000, // MCr4
    tonnage: 2,
    power: 1,
    techLevel: 13,
    properties: {
      jamDM: 4,
      cannotJamMeson: true,
    },
  },
  // Military Countermeasures Suite
  {
    id: "militaryCountermeasuresSuite",
    type: "sensor",
    subtype: "militaryCountermeasuresSuite",
    name: "Military Countermeasures Suite",
    description: "DM+6 to all jamming/electronic warfare. TL15.",
    cost: 28000000, // MCr28
    tonnage: 15,
    power: 2,
    techLevel: 15,
    properties: {
      jamDM: 6,
    },
  },
  // Deep Penetration Scanners
  {
    id: "deepPenetrationScanners",
    type: "sensor",
    subtype: "deepPenetrationScanners",
    name: "Deep Penetration Scanners",
    description:
      "NAS/densitometer, scans 20 tons of target per hour per ton. TL13. MCr1/ton, 1 power/ton.",
    cost: 1000000, // MCr1 per ton (set tonnage at selection)
    tonnage: 1, // per ton
    power: 1, // per ton
    techLevel: 13,
    properties: {
      scanRate: 20, // tons/hour/ton
      costPerTon: 1000000,
      powerPerTon: 1,
    },
  },
  // Distributed Array
  {
    id: "distributedArray",
    type: "sensor",
    subtype: "distributedArray",
    name: "Distributed Array",
    description:
      "For Improved/Advanced suites, >5000 tons. Triples tonnage, cost, power of attached suite. TL11.",
    cost: 0, // Calculated
    tonnage: 0, // Calculated
    power: 0, // Calculated
    techLevel: 11,
    properties: {
      multiplier: 3,
      requiresImprovedOrAdvanced: true,
      minShipTonnage: 5000,
    },
  },
  // Extended Array
  {
    id: "extendedArray",
    type: "sensor",
    subtype: "extendedArray",
    name: "Extended Array",
    description:
      "Distributed array on retractable arms. Triples tonnage, cost, triples power. TL11. No thrust/jump while deployed, +2 DM to detect.",
    cost: 0, // Calculated
    tonnage: 0, // Calculated
    power: 0, // Calculated
    techLevel: 11,
    properties: {
      multiplier: 3,
      extended: true,
      disablesThrustJump: true,
      detectDM: 2,
    },
  },
  // Extension Net
  {
    id: "extensionNet",
    type: "sensor",
    subtype: "extensionNet",
    name: "Extension Net",
    description:
      "1% ship tonnage (min 1 ton), MCr1/ton. Increases sensor range by 1 step. TL10.",
    cost: 1000000, // MCr1 per ton (set tonnage at selection)
    tonnage: 1, // min 1 ton
    power: 0,
    techLevel: 10,
    properties: {
      percentOfShip: 1,
      minTonnage: 1,
      costPerTon: 1000000,
      rangeStep: 1,
    },
  },
  // Life Scanner
  {
    id: "lifeScanner",
    type: "sensor",
    subtype: "lifeScanner",
    name: "Life Scanner",
    description:
      "Detects and differentiates life forms. TL12. 1 ton, MCr2, 1 power.",
    cost: 2000000, // MCr2
    tonnage: 1,
    power: 1,
    techLevel: 12,
    properties: {},
  },
  // Life Scanner Analysis Suite
  {
    id: "lifeScannerAnalysisSuite",
    type: "sensor",
    subtype: "lifeScannerAnalysisSuite",
    name: "Life Scanner Analysis Suite",
    description:
      "Advanced life scanner, adds environmental/biological analysis. TL14. 1 ton, MCr4, 1 power.",
    cost: 4000000, // MCr4
    tonnage: 1,
    power: 1,
    techLevel: 14,
    properties: {},
  },
  // Mail Distribution Array (TL10)
  {
    id: "mailDistributionArray10",
    type: "sensor",
    subtype: "mailDistributionArray10",
    name: "Mail Distribution Array (TL10)",
    description: "Handles x-boat mail data. TL10. 10 tons, MCr20.",
    cost: 20000000, // MCr20
    tonnage: 10,
    power: 0,
    techLevel: 10,
    properties: {},
  },
  // Mail Distribution Array (TL13)
  {
    id: "mailDistributionArray13",
    type: "sensor",
    subtype: "mailDistributionArray13",
    name: "Mail Distribution Array (TL13)",
    description: "Handles x-boat mail data. TL13. 20 tons, MCr10.",
    cost: 10000000, // MCr10
    tonnage: 20,
    power: 0,
    techLevel: 13,
    properties: {},
  },
  // Mineral Detection Suite
  {
    id: "mineralDetectionSuite",
    type: "sensor",
    subtype: "mineralDetectionSuite",
    name: "Mineral Detection Suite",
    description:
      "Upgrades densitometer for mineral detection. TL12. 1 ton, MCr5.",
    cost: 5000000, // MCr5
    tonnage: 1,
    power: 0,
    techLevel: 12,
    properties: {},
  },
  // Rapid-Deployment Extended Array
  {
    id: "rapidDeploymentExtendedArray",
    type: "sensor",
    subtype: "rapidDeploymentExtendedArray",
    name: "Rapid-Deployment Extended Array",
    description:
      "Like extended array, but can deploy/retract in seconds. Triples tonnage, 5x cost, triples power. TL11.",
    cost: 0, // Calculated
    tonnage: 0, // Calculated
    power: 0, // Calculated
    techLevel: 11,
    properties: {
      multiplier: 3,
      costMultiplier: 5,
      rapidDeployment: true,
    },
  },
  // Shallow Penetration Suite
  {
    id: "shallowPenetrationSuite",
    type: "sensor",
    subtype: "shallowPenetrationSuite",
    name: "Shallow Penetration Suite",
    description: "Thermal/EM hull penetration, 10 tons, MCr5, 1 power. TL10.",
    cost: 5000000, // MCr5
    tonnage: 10,
    power: 1,
    techLevel: 10,
    properties: {},
  },
  // Improved Signal Processing
  {
    id: "improvedSignalProcessing",
    type: "sensor",
    subtype: "improvedSignalProcessing",
    name: "Improved Signal Processing",
    description:
      "DM+2 to sensor checks, but double DM for jamming. TL11. 1 ton, MCr4, 1 power.",
    cost: 4000000, // MCr4
    tonnage: 1,
    power: 1,
    techLevel: 11,
    properties: {
      sensorDM: 2,
      doubleJammingDM: true,
    },
  },
  // Enhanced Signal Processing
  {
    id: "enhancedSignalProcessing",
    type: "sensor",
    subtype: "enhancedSignalProcessing",
    name: "Enhanced Signal Processing",
    description:
      "DM+4 to sensor checks, no jamming vulnerability. TL13. 2 tons, MCr8, 2 power.",
    cost: 8000000, // MCr8
    tonnage: 2,
    power: 2,
    techLevel: 13,
    properties: {
      sensorDM: 4,
    },
  },
  // Internal Systems
  {
    id: "internalAirlock",
    type: "internal",
    subtype: "internalAirlock",
    name: "Additional Airlock",
    description:
      "Additional airlock for ship access. Minimum 2 tons, MCr0.1 per ton. Larger airlocks can be used for cargo bays.",
    cost: 0, // Calculated: MCr0.1 per ton
    tonnage: 2, // Minimum 2 tons (user configurable)
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 100000,
      minTonnage: 2,
      configurableTonnage: true,
    },
  },
  {
    id: "internalArmoury",
    type: "internal",
    subtype: "internalArmoury",
    name: "Armoury",
    description:
      "Specialised weapons storage facility. 1 ton per 25 crew or 5 marines. MCr0.25 per ton.",
    cost: 0, // Calculated: MCr0.25 per ton
    tonnage: 1, // User configurable
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 250000,
      crewPerTon: 25,
      marinesPerTon: 5,
      configurableTonnage: true,
    },
  },
  {
    id: "internalBiosphere",
    type: "internal",
    subtype: "internalBiosphere",
    name: "Biosphere",
    description:
      "Area dedicated to flora/fauna for food or leisure. 1 ton eliminates life support cost for 2 passengers. 1 power per ton. MCr0.2 per ton.",
    cost: 0, // Calculated: MCr0.2 per ton
    tonnage: 1, // User configurable
    power: 1, // Per ton
    techLevel: 8,
    properties: {
      costPerTon: 200000,
      powerPerTon: 1,
      supportsPassengersPerTon: 2,
      configurableTonnage: true,
    },
  },
  {
    id: "internalBoobyTrap",
    type: "internal",
    subtype: "internalBoobyTrap",
    name: "Booby-Trapped Airlock",
    description:
      "Existing airlock fitted with lethal defensive equipment. No tonnage. TL6+.",
    cost: 0, // Calculated by type
    tonnage: 0,
    power: 0,
    techLevel: 6,
    properties: {
      boobyTrapType: "basic", // basic, improved, enhanced, advanced
      types: [
        { label: "Basic", tl: 6, cost: 100000, damage: "3D" },
        { label: "Improved", tl: 8, cost: 300000, damage: "5D" },
        { label: "Enhanced", tl: 10, cost: 500000, damage: "6D" },
        { label: "Advanced", tl: 12, cost: 1000000, damage: "8D" },
      ],
      noTonnage: true,
      configurableType: true,
    },
  },
  {
    id: "internalBriefingRoom",
    type: "internal",
    subtype: "internalBriefingRoom",
    name: "Briefing Room",
    description:
      "Specialised room for mission planning. 4 tons, MCr0.5. Grants DM+1 to Tactics (military) checks.",
    cost: 500000,
    tonnage: 4,
    power: 0,
    techLevel: 8,
    properties: {
      tacticsDM: 1,
    },
  },
  {
    id: "internalConcealedCompartment",
    type: "internal",
    subtype: "internalConcealedCompartment",
    name: "Concealed Compartment",
    description:
      "Hidden, shielded compartment for contraband. Up to 5% of ship tonnage. DM-2 to Sensors, DM-4 to Investigate. Cr20,000 per ton.",
    cost: 0, // Calculated: Cr20,000 per ton
    tonnage: 1, // User configurable
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 20000,
      maxPercentOfShip: 5,
      sensorDM: -2,
      investigateDM: -4,
      configurableTonnage: true,
    },
  },
  {
    id: "internalConstructionDeck",
    type: "internal",
    subtype: "internalConstructionDeck",
    name: "Construction Deck",
    description:
      "Mobile shipyard for repair/building small ships. Can build ship up to half its tonnage. 1 power per ton. MCr0.5 per ton.",
    cost: 0, // Calculated: MCr0.5 per ton
    tonnage: 1, // User configurable
    power: 1, // Per ton
    techLevel: 8,
    properties: {
      costPerTon: 500000,
      powerPerTon: 1,
      buildFraction: 0.5,
      configurableTonnage: true,
    },
  },
  {
    id: "internalDockingSpace",
    type: "internal",
    subtype: "internalDockingSpace",
    name: "Docking Space",
    description:
      "Internal bay for auxiliary ship/vehicle. Tonnage = largest ship + 10%. MCr0.25 per ton.",
    cost: 0, // Calculated
    tonnage: 1, // User configurable
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 250000,
      extraPercent: 10,
      configurableTonnage: true,
    },
  },
  {
    id: "internalFullHangar",
    type: "internal",
    subtype: "internalFullHangar",
    name: "Full Hangar",
    description:
      "Allows repairs/maintenance of craft on board. Tonnage = 2x craft. MCr0.2 per ton.",
    cost: 0, // Calculated
    tonnage: 1, // User configurable
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 200000,
      tonnageMultiplier: 2,
      configurableTonnage: true,
    },
  },
  {
    id: "internalGravScreen",
    type: "internal",
    subtype: "internalGravScreen",
    name: "Grav Screen",
    description:
      "Blocks densitometers from scanning inside ship. 1 ton per 200 tons hull. MCr1 per ton. 2 power per ton. TL12.",
    cost: 0, // Calculated
    tonnage: 1, // User configurable
    power: 2, // Per ton
    techLevel: 12,
    properties: {
      costPerTon: 1000000,
      powerPerTon: 2,
      tonsPerHull: 200,
      configurableTonnage: true,
    },
  },
  {
    id: "internalLaboratory",
    type: "internal",
    subtype: "internalLaboratory",
    name: "Laboratory",
    description:
      "Space for research/experimentation. 4 tons per scientist. MCr1 per 4 tons.",
    cost: 0, // Calculated
    tonnage: 4, // User configurable
    power: 0,
    techLevel: 8,
    properties: {
      costPer4Tons: 1000000,
      tonsPerScientist: 4,
      configurableTonnage: true,
    },
  },
  {
    id: "internalLaunchTube",
    type: "internal",
    subtype: "internalLaunchTube",
    name: "Launch Tube",
    description:
      "Rapid launch for small craft. Tonnage = 10x largest craft. MCr0.5 per ton. 1 power per ton. TL9.",
    cost: 0, // Calculated
    tonnage: 1, // User configurable
    power: 1, // Per ton
    techLevel: 9,
    properties: {
      costPerTon: 500000,
      powerPerTon: 1,
      tonnageMultiplier: 10,
      configurableTonnage: true,
    },
  },
  {
    id: "internalLibrary",
    type: "internal",
    subtype: "internalLibrary",
    name: "Library",
    description:
      "Computer files, holotanks, books. 4 tons, MCr4. TL8. Grants DM+1 to EDU checks for training.",
    cost: 4000000,
    tonnage: 4,
    power: 0,
    techLevel: 8,
    properties: {
      educationDM: 1,
    },
  },
  {
    id: "internalReEntryCapsule",
    type: "internal",
    subtype: "internalReEntryCapsule",
    name: "Re-entry Capsule",
    description: "Emergency escape capsule. 0.5 tons, Cr20,000. TL8.",
    cost: 20000,
    tonnage: 0.5,
    power: 0,
    techLevel: 8,
    properties: {},
  },
  {
    id: "internalAssaultCapsule",
    type: "internal",
    subtype: "internalAssaultCapsule",
    name: "Assault Capsule",
    description:
      "Infantry deployment capsule. 0.5 tons, Cr50,000. TL10. Protection +20, -2 DM to Sensors.",
    cost: 50000,
    tonnage: 0.5,
    power: 0,
    techLevel: 10,
    properties: {
      protection: 20,
      sensorDM: -2,
    },
  },
  {
    id: "internalHighSurvivabilityCapsule",
    type: "internal",
    subtype: "internalHighSurvivabilityCapsule",
    name: "High Survivability Capsule",
    description:
      "Heavily armoured, decoy-deploying capsule. 0.5 tons, MCr0.1. TL14. Protection +30, -4 DM to Sensors, -2 DM to attack rolls.",
    cost: 100000,
    tonnage: 0.5,
    power: 0,
    techLevel: 14,
    properties: {
      protection: 30,
      sensorDM: -4,
      attackDM: -2,
    },
  },
  {
    id: "internalReEntryPod",
    type: "internal",
    subtype: "internalReEntryPod",
    name: "Re-entry Pod",
    description:
      "Gliding, computer-guided pod for 2 occupants. 1 ton, MCr0.15. TL9.",
    cost: 150000,
    tonnage: 1,
    power: 0,
    techLevel: 9,
    properties: {
      occupantCapacity: 2,
    },
  },
  {
    id: "internalRecoveryDeck",
    type: "internal",
    subtype: "internalRecoveryDeck",
    name: "Recovery Deck",
    description:
      "Rapid recovery of small craft. Tonnage = 10x largest craft. MCr0.5 per ton. 1 power per ton.",
    cost: 0, // Calculated
    tonnage: 1, // User configurable
    power: 1, // Per ton
    techLevel: 8,
    properties: {
      costPerTon: 500000,
      powerPerTon: 1,
      tonnageMultiplier: 10,
      configurableTonnage: true,
    },
  },
  {
    id: "internalStable",
    type: "internal",
    subtype: "internalStable",
    name: "Stable",
    description:
      "Low-grade housing for animals/slaves. Min 10 tons. Cr2,500 per ton. Cr250 per ton for life support.",
    cost: 25000, // 10 tons × Cr2,500 per ton
    tonnage: 10, // Minimum 10 tons
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 2500,
      minTonnage: 10,
      lifeSupportPerTon: 250,
      configurableTonnage: false, // No longer configurable, just stack them
    },
  },
  {
    id: "internalStudio",
    type: "internal",
    subtype: "internalStudio",
    name: "Studio",
    description: "Space for Profession skill. 4 tons per user. MCr0.1 per ton.",
    cost: 0, // Calculated
    tonnage: 4, // User configurable
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 100000,
      tonsPerUser: 4,
      configurableTonnage: true,
    },
  },
  {
    id: "internalTrainingFacilities",
    type: "internal",
    subtype: "internalTrainingFacilities",
    name: "Training Facilities",
    description:
      "Combat training, gyms, firing ranges. 2 tons per trainee. MCr0.2 per ton.",
    cost: 0, // Calculated
    tonnage: 2, // User configurable
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 200000,
      tonsPerTrainee: 2,
      configurableTonnage: true,
    },
  },
  {
    id: "internalUnrepSystem",
    type: "internal",
    subtype: "internalUnrepSystem",
    name: "UNREP System",
    description:
      "Allows replenishment/resupply in motion. 1 ton allows transfer of 20 tons/hour. MCr0.5 per ton. 1 power per ton.",
    cost: 0, // Calculated
    tonnage: 1, // User configurable
    power: 1, // Per ton
    techLevel: 8,
    properties: {
      costPerTon: 500000,
      powerPerTon: 1,
      transferPerTon: 20,
      configurableTonnage: true,
    },
  },
  {
    id: "internalVault",
    type: "internal",
    subtype: "internalVault",
    name: "Vault",
    description:
      "Armoured, sealed chamber for survival. 4-40 tons. MCr0.5 per ton. 1 armour hit per ton (max 10). 1 hit per 5 tons.",
    cost: 0, // Calculated
    tonnage: 4, // User configurable
    power: 0,
    techLevel: 8,
    properties: {
      costPerTon: 500000,
      minTonnage: 4,
      maxTonnage: 40,
      armourPerTon: 1,
      maxArmour: 10,
      hitsPer5Tons: 1,
      configurableTonnage: true,
    },
  },
  {
    id: "internalWorkshop",
    type: "internal",
    subtype: "internalWorkshop",
    name: "Workshop",
    description:
      "Repair/fabrication space. 6 tons, MCr0.9. Allows 2 Travellers to use Mechanic skill with DM+2.",
    cost: 900000,
    tonnage: 6,
    power: 0,
    techLevel: 8,
    properties: {
      users: 2,
      mechanicDM: 2,
    },
  },
  {
    id: "dropTank",
    type: "fuel",
    subtype: "dropTank",
    name: "Drop Tank",
    description:
      "External tank jettisoned before jump. Ship cannot be streamlined.",
    cost: 25000, // Per ton of tank (Cr25,000)
    tonnage: 1, // Per ton of tank (user input)
    power: 0,
    techLevel: 12,
    properties: {
      mountTonnagePercent: 0.004, // 0.4% of tank tonnage
      mountCostPerTon: 500000, // MCr0.5 per ton of mount
      tankCostPerTon: 25000, // Cr25,000 per ton of tank
      userSelectableTonnage: true, // User can specify tank size
    },
  },
  {
    id: "fuelCargoContainer",
    type: "fuel",
    subtype: "fuelCargoContainer",
    name: "Fuel/Cargo Container",
    description: "Container for fuel or cargo, with additional equipment.",
    cost: 50000, // Cr5,000 per ton (Rules Reference: Cr5,000 per ton)
    tonnage: 10.5, // 10 + 5% equipment
    power: 0,
    techLevel: 12,
    properties: { capacity: 10, additionalEquipment: 0.5 },
  },
  {
    id: "fuelProcessor",
    type: "fuel",
    subtype: "fuelProcessor",
    name: "Fuel Processor",
    description: "Processes unrefined fuel. 20 tons/day per ton of processor.",
    cost: 500000, // Cr50,000 per ton (Rules Reference: Cr50,000 per ton)
    tonnage: 1, // per unit
    power: 1, // per ton
    techLevel: 12,
    properties: { processingRate: 20 },
  },
  {
    id: "fuelScoop",
    type: "fuel",
    subtype: "fuelScoop",
    name: "Fuel Scoop",
    description:
      "Allows fuel collection from gas giants. Required for unstreamlined hulls.",
    cost: 1000000, // MCr1, update as needed
    tonnage: 0, // No tonnage required
    power: 0,
    techLevel: 12,
    properties: {},
  },
  {
    id: "fuelTankCompartment",
    type: "fuel",
    subtype: "fuelTankCompartment",
    name: "Fuel Tank Compartment",
    description:
      "Hidden compartment within fuel tanks. Only accessible when tank is 75% empty.",
    cost: 4000, // Cr4,000 per ton (Rules Reference: Cr4,000 per ton)
    tonnage: 1, // per unit
    power: 0,
    techLevel: 12,
    properties: { detectionPenalty: { sensors: -4, investigate: -6 } },
  },
  {
    id: "metalHydride",
    type: "fuel",
    subtype: "metalHydride",
    name: "Metal Hydride Storage",
    description:
      "Safer storage at room temperature. Takes twice the space of normal tanks.",
    cost: 0, // Set to 0, actual cost calculated globally in summary
    tonnage: 0, // or undefined, as it doesn't consume extra space
    power: 0,
    techLevel: 12,
    properties: {},
  },
  {
    id: "mountableTank",
    type: "fuel",
    subtype: "mountableTank",
    name: "Mountable Tank",
    description: "Converts cargo space to fuel tanks. Cr1000 per ton.",
    cost: 1000000, // Cr1,000 per ton (Rules Reference: Cr1,000 per ton)
    tonnage: 1, // per unit
    power: 0,
    techLevel: 12,
    properties: {},
  },
  {
    id: "ramscoop",
    type: "fuel",
    subtype: "ramscoop",
    name: "Ramscoop",
    description:
      "Collects fuel from gas giants. Not compatible with streamlined hulls.",
    cost: 250000, // MCr0.25 per ton (Rules Reference: MCr0.25 per ton)
    tonnage: 10, // min 10 tons
    power: 0,
    techLevel: 12,
    properties: { collectionRate: 5 }, // 5 tons/week per ton (Rules Reference)
  },
  // Common Area Systems
  {
    id: "advancedEntertainment",
    type: "commonArea",
    subtype: "advancedEntertainment",
    name: "Advanced Entertainment System",
    description:
      "Audio/visual/VR entertainment for the common area. No tonnage required. Cost varies by tech level and sophistication.",
    cost: 1000, // Minimum, can be overridden
    tonnage: 0,
    power: 0,
    techLevel: 5,
    properties: { minCost: 100, maxCost: 10000 },
  },
  {
    id: "brewery",
    type: "commonArea",
    subtype: "brewery",
    name: "Brewery or Distillery",
    description:
      "Microbrewery/distillery for producing beer or liquor. 0.5 tons per 10L/week. TL10+.",
    cost: 100000,
    tonnage: 0.5,
    power: 0,
    techLevel: 10,
    properties: { tonsPerBatch: 0.5, batchSize: 10, costPerTon: 100000 },
  },
  {
    id: "gourmetKitchen",
    type: "commonArea",
    subtype: "gourmetKitchen",
    name: "Gourmet Kitchen",
    description:
      "Fine culinary facilities. 1 ton per diner. MCr0.2 per ton. Requires Steward 2.",
    cost: 200000,
    tonnage: 1,
    power: 0,
    techLevel: 8,
    properties: { costPerTon: 200000, requiresSteward: 2 },
  },
  {
    id: "hotTub",
    type: "commonArea",
    subtype: "hotTub",
    name: "Hot Tub",
    description: "Relaxing hot tub. 0.25 tons per Traveller. Cr12000 per ton.",
    cost: 12000,
    tonnage: 0.25,
    power: 0,
    techLevel: 8,
    properties: { tonsPerTraveller: 0.25, costPerTon: 12000 },
  },
  {
    id: "swimmingPool",
    type: "commonArea",
    subtype: "swimmingPool",
    name: "Swimming Pool (Standard)",
    description: "",
    cost: 20000,
    tonnage: 4,
    power: 0,
    techLevel: 8,
    properties: {
      minTonnage: 4,
      costPerTon: 20000,
      poolType: "standard" as "standard",
    },
  },
  {
    id: "endlessPool",
    type: "commonArea",
    subtype: "endlessPool",
    name: "Swimming Pool (Endless)",
    description: "Endless pool for exercise/rehab. 2 tons. Cr50000 per ton.",
    cost: 50000,
    tonnage: 2,
    power: 0,
    techLevel: 10,
    properties: {
      minTonnage: 2,
      costPerTon: 50000,
      poolType: "endless" as "endless",
    },
  },
  {
    id: "theatreBasic",
    type: "commonArea",
    subtype: "theatreBasic",
    name: "Theatre (Basic)",
    description: "",
    cost: 100000,
    tonnage: 8,
    power: 0,
    techLevel: 8,
    properties: { minTonnage: 8, costPerTon: 100000 },
  },
  {
    id: "theatreAdvanced",
    type: "commonArea",
    subtype: "theatreAdvanced",
    name: "Theatre (Advanced)",
    description:
      "Advanced theatre with holographics, sound, etc. Min 12 tons. MCr0.2 per ton.",
    cost: 200000,
    tonnage: 12,
    power: 0,
    techLevel: 12,
    properties: { minTonnage: 12, costPerTon: 200000 },
  },
  {
    id: "wetBar",
    type: "commonArea",
    subtype: "wetBar",
    name: "Wet Bar",
    description:
      "Basic wet bar, no tonnage required. Cr2000 (not including beverages).",
    cost: 2000,
    tonnage: 0,
    power: 0,
    techLevel: 8,
    properties: {},
  },
  {
    id: "zeroGRoom",
    type: "commonArea",
    subtype: "zeroGRoom",
    name: "Zero-G Room",
    description:
      "Enclosed zero-G room. Any size. Controls/portal cost Cr50000.",
    cost: 50000,
    tonnage: 1,
    power: 0,
    techLevel: 10,
    properties: { minTonnage: 1, costPerPortal: 50000 },
  },
];

export const COMMON_AREA_SYSTEMS = [
  {
    type: "commonArea",
    subtype: "advancedEntertainment",
    name: "Advanced Entertainment System",
    description:
      "Audio/visual/VR entertainment for the common area. No tonnage required. Cost varies by tech level and sophistication.",
    tonnage: 0,
    cost: 1000, // Minimum, can be overridden
    techLevel: 5,
    properties: { minCost: 100, maxCost: 10000 },
  },
  {
    type: "commonArea",
    subtype: "brewery",
    name: "Brewery or Distillery",
    description:
      "Microbrewery/distillery for producing beer or liquor. 0.5 tons per 10L/week. TL10+.",
    tonnage: 0.5,
    cost: 100000,
    techLevel: 10,
    properties: { tonsPerBatch: 0.5, batchSize: 10, costPerTon: 100000 },
  },
  {
    type: "commonArea",
    subtype: "gourmetKitchen",
    name: "Gourmet Kitchen",
    description:
      "Fine culinary facilities. 1 ton per diner. MCr0.2 per ton. Requires Steward 2.",
    tonnage: 1,
    cost: 200000,
    techLevel: 8,
    properties: { costPerTon: 200000, requiresSteward: 2 },
  },
  {
    type: "commonArea",
    subtype: "hotTub",
    name: "Hot Tub",
    description: "Relaxing hot tub. 0.25 tons per Traveller. Cr12000 per ton.",
    tonnage: 0.25,
    cost: 12000,
    techLevel: 8,
    properties: { tonsPerTraveller: 0.25, costPerTon: 12000 },
  },
  {
    type: "commonArea",
    subtype: "swimmingPool",
    name: "Swimming Pool (Standard)",
    description: "",
    tonnage: 4,
    cost: 20000,
    techLevel: 8,
    properties: {
      minTonnage: 4,
      costPerTon: 20000,
      poolType: "standard" as "standard",
    },
  },
  {
    type: "commonArea",
    subtype: "endlessPool",
    name: "Swimming Pool (Endless)",
    description: "Endless pool for exercise/rehab. 2 tons. Cr50000 per ton.",
    tonnage: 2,
    cost: 50000,
    techLevel: 10,
    properties: {
      minTonnage: 2,
      costPerTon: 50000,
      poolType: "endless" as "endless",
    },
  },
  {
    type: "commonArea",
    subtype: "theatreBasic",
    name: "Theatre (Basic)",
    description: "",
    tonnage: 8,
    cost: 100000,
    techLevel: 8,
    properties: { minTonnage: 8, costPerTon: 100000 },
  },
  {
    type: "commonArea",
    subtype: "theatreAdvanced",
    name: "Theatre (Advanced)",
    description:
      "Advanced theatre with holographics, sound, etc. Min 12 tons. MCr0.2 per ton.",
    tonnage: 12,
    cost: 200000,
    techLevel: 12,
    properties: { minTonnage: 12, costPerTon: 200000 },
  },
  {
    type: "commonArea",
    subtype: "wetBar",
    name: "Wet Bar",
    description:
      "Basic wet bar, no tonnage required. Cr2000 (not including beverages).",
    tonnage: 0,
    cost: 2000,
    techLevel: 8,
    properties: {},
  },
  {
    type: "commonArea",
    subtype: "zeroGRoom",
    name: "Zero-G Room",
    description:
      "Enclosed zero-G room. Any size. Controls/portal cost Cr50000.",
    tonnage: 1,
    cost: 50000,
    techLevel: 10,
    properties: { minTonnage: 1, costPerPortal: 50000 },
  },
];
